import { observer } from 'mobx-react-lite';
import Skeleton from 'react-loading-skeleton';

import { cn } from '@workspace/ui/lib/utils';

import {
  getChoiceImage,
  hasSwatchImages,
} from '~/components/VariantSwatchGrid';
import { useProductChoices } from '~/hooks/useProductChoices';
import type { ProductViewProps } from '~/pages/Product/Product';
import type { ProductFilterChoice } from '~/types/Product/interface';

type Props = Pick<
  ProductViewProps,
  'product' | 'handleChangeOffer' | 'isLoading'
> & {
  className?: string;
};

const isChoiceAvailable = (choice: ProductFilterChoice) =>
  choice.targetSellable ?? Boolean(choice.sellable);

/**
 * Variant selectors of the DubaiExpress product page: 56px photo swatches
 * for properties with pictures, 32px colour discs for colour lists, 32px text
 * chips otherwise. A value is dimmed (a disc is also struck through) only when
 * the offer it switches to is out of stock; a value missing in the current
 * combination still looks available, since picking it moves the rest of the
 * selection to an existing offer. The disc ring is `outline-*!` because the
 * global `* { outline: 0 }` in `_base.scss` beats layered Tailwind utilities.
 */
const ProductDEVariantsView = ({
  product,
  handleChangeOffer,
  isLoading,
  className,
}: Props) => {
  const filters = useProductChoices({ product });

  if (isLoading) {
    return (
      <div className={cn('flex gap-1', className)}>
        <Skeleton width={74} height={32} className="rounded-[10px]!" />
        <Skeleton width={74} height={32} className="rounded-[10px]!" />
      </div>
    );
  }

  const list = Object.values(filters)
    .filter((filter) => filter.choices.length > 0)
    .sort((a, b) => a.sorting - b.sorting || a.order - b.order);

  if (!list.length) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {list.map((filter) => {
        const selectedChoice = filter.choices.find((choice) => choice.selected);
        const inputName = `${filter.code}_variant`;
        const withImages = hasSwatchImages(filter);
        const isColor = filter.type === 'colorList';

        return (
          <div key={filter.code}>
            <div className="mb-2 font-medium text-[#8A8A8A] text-[14px] leading-5">
              {filter.name}:{' '}
              <span className="text-[#141415]">{selectedChoice?.label}</span>
            </div>
            <div
              className={cn(
                withImages
                  ? 'grid grid-cols-[repeat(6,56px)] gap-1'
                  : 'flex flex-wrap gap-1',
              )}
            >
              {filter.choices.map((choice) => {
                const outOfStock = !isChoiceAvailable(choice);
                const image = withImages ? getChoiceImage(choice) : '';
                const isSwatch = isColor && !withImages;

                return (
                  <label
                    key={choice.value}
                    data-choice-value={choice.value}
                    data-type={filter.type}
                    title={choice.label}
                    className={cn(
                      'box-border cursor-pointer border border-[#E1E1E1] border-solid has-focus-visible:ring-2 has-focus-visible:ring-[#141415] has-focus-visible:ring-offset-2',
                      withImages &&
                        'flex size-14 items-center justify-center overflow-hidden rounded-[12px] bg-white p-0',
                      !withImages &&
                        !isColor &&
                        'inline-flex h-8 items-center justify-center whitespace-nowrap rounded-[10px] bg-white px-3.5 font-medium text-[#141415] text-[14px] leading-none',
                      isSwatch &&
                        'relative inline-flex size-8 rounded-full border-2 border-white outline-solid!',
                      isSwatch &&
                        (choice.selected
                          ? 'outline-2! outline-[#E94E1A]!'
                          : 'outline-1! outline-[#E1E1E1]!'),
                      !isSwatch &&
                        outOfStock &&
                        (withImages
                          ? 'opacity-40'
                          : 'bg-[#F4F4F4] text-[#B0B0B0]'),
                      !isSwatch &&
                        choice.selected &&
                        'border-2 border-[#E94E1A] text-[#E94E1A]',
                    )}
                  >
                    <input
                      data-radio={choice.label}
                      type="radio"
                      name={inputName}
                      id={`${inputName}_${choice.value}`}
                      value={String(choice.value)}
                      checked={Boolean(choice.selected)}
                      className="sr-only"
                      onChange={() => {
                        if (choice.sku) {
                          handleChangeOffer(choice.sku)();
                        }
                      }}
                    />
                    {isSwatch && (
                      <span
                        className={cn(
                          'size-full rounded-full',
                          outOfStock && 'opacity-40',
                        )}
                        style={{ backgroundColor: String(choice.value) }}
                      />
                    )}
                    {isSwatch && outOfStock && (
                      <span className="absolute inset-0 m-auto h-7 w-0.5 rotate-45 bg-[#141415]" />
                    )}
                    {withImages &&
                      (image ? (
                        <img
                          src={image}
                          alt={choice.label}
                          loading="lazy"
                          className="size-full object-cover"
                        />
                      ) : (
                        <span className="overflow-hidden px-0.5 text-center font-medium text-[12px] leading-4">
                          {choice.label}
                        </span>
                      ))}
                    {!withImages && !isColor && choice.label}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const ProductDEVariants = observer(ProductDEVariantsView);
